<script setup lang="ts">
/**
 * Модальное окно администрирования порогов вероятности и визуализации матрицы рисков.
 *
 * Функционал:
 * - Управление порогами вероятности нежелательных событий (AdverseEventProbabilityThreshold);
 * - Создание, редактирование и удаление порогов через диалоговое окно ProbabilityThresholdFormDialog;
 * - Визуализация интерактивной матрицы рисков 5×5 (Вероятность × Последствия = Уровень риска)
 *   в строгом соответствии с бизнес-правилами класса RiskManager на бэкенде;
 * - Легенда цветового зонирования рисков (Высокий / Средний / Низкий).
 */

// Импорты Vue
import { computed, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

// Импорты UI-компонентов
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import ProbabilityThresholdFormDialog from './ProbabilityThresholdFormDialog.vue'

// Импорты хранилищ
import { useAdverseSettingsStore } from '../../stores/adverseSettingsStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты типов
import type { IAdverseProbabilityThreshold, IRiskMatrixCell } from '../../types/IAdverseSettings'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { ADVERSE_ANCHORS } from '@/modules/onboarding/scenarios/adverseEvents/anchors'

/**
 * Входящие параметры модального окна
 */
const props = defineProps<{
  /** Управление видимостью диалогового окна */
  modelValue: boolean
}>()

/**
 * События компонента
 */
const emit = defineEmits<{
  /** Обновление состояния видимости (v-model) */
  'update:modelValue': [value: boolean]
}>()

const { smAndDown } = useDisplay()
const adverseSettingsStore = useAdverseSettingsStore()
const feedbackStore = useFeedbackStore()

/**
 * Активная вкладка модального окна:
 * 0 - Пороги вероятности
 * 1 - Матрица рисков 5х5
 */
const activeTab = ref<number>(0)

/** Флаг видимости формы создания/редактирования порога */
const isFormDialogOpen = ref(false)

/** Редактируемый порог (null — создание нового) */
const editingThreshold = ref<IAdverseProbabilityThreshold | null>(null)

/** Флаг диалога подтверждения удаления порога */
const isDeleteConfirmOpen = ref(false)

/** Порог, намеченный к удалению */
const thresholdToDelete = ref<IAdverseProbabilityThreshold | null>(null)

/** Список всех настроенных порогов вероятности */
const thresholds = computed(() => adverseSettingsStore.probabilityThresholds)

/** Список занятых уровней вероятности */
const occupiedProbabilities = computed(() => thresholds.value.map(t => t.probability))

/** Можно ли добавить еще один порог (максимум 5 уровней: 0..4) */
const canAddThreshold = computed(() => thresholds.value.length < 5)



// ==========================================
// Конфигурация уровней вероятности и последствий
// ==========================================

/** Справочник уровней вероятности */
const probabilityLevels = [
  { value: 4, label: 'Высокая' },
  { value: 3, label: 'Выше средней' },
  { value: 2, label: 'Средняя' },
  { value: 1, label: 'Низкая' },
  { value: 0, label: 'Практически исключено' },
]

/** Справочник уровней последствий */
const consequenceLevels = [
  { value: 0, label: 'Несущественные' },
  { value: 1, label: 'Легкие' },
  { value: 2, label: 'Средние' },
  { value: 3, label: 'Значительные' },
  { value: 4, label: 'Катастрофические' },
]

/**
 * Расчет уровня риска по матрице RiskManager (источник истины — backend risk_manager.py)
 *
 * @param probability Уровень вероятности (0..4)
 * @param consequence Уровень последствий (0..4)
 * @returns Данные ячейки матрицы рисков (уровень, название, цвет)
 */
function calculateRiskLevel(probability: number, consequence: number): IRiskMatrixCell {
  // HIGH RISK (#f44336)
  if (
    (probability >= 2 && consequence === 4)
    || (probability >= 3 && consequence >= 3)
    || (probability === 4 && consequence >= 2)
  ) {
    return {
      probability,
      consequence,
      riskLevel: 3,
      riskName: 'Высокий',
      riskColor: '#f44336',
    }
  }

  // MEDIUM RISK (#ff9800)
  if (
    (probability === 4 && consequence <= 1)
    || (probability === 3 && consequence <= 2)
    || (probability === 2 && consequence >= 1 && consequence <= 3)
    || (probability === 1 && consequence >= 2)
    || (probability === 0 && consequence === 4)
  ) {
    return {
      probability,
      consequence,
      riskLevel: 2,
      riskName: 'Средний',
      riskColor: '#ff9800',
    }
  }

  // LOW RISK (#4caf50)
  return {
    probability,
    consequence,
    riskLevel: 1,
    riskName: 'Низкий',
    riskColor: '#4caf50',
  }
}

/**
 * Получить метаданные уровня вероятности по его коду
 *
 * @param prob Числовой код вероятности (0..4)
 */
function getProbabilityMeta(prob: number) {
  return probabilityLevels.find(p => p.value === prob) || {
    value: prob,
    label: `Уровень ${prob}`,
  }
}

/**
 * Загрузка данных при открытии модального окна
 */
async function loadData() {
  try {
    await Promise.all([
      adverseSettingsStore.fetchProbabilityThresholds(),
      adverseSettingsStore.fetchProbabilityAndConsequence(),
    ])
  } catch (error) {
    console.error('❌ Ошибка при загрузке данных настроек вероятности:', error)
  }
}

watch(
  () => props.modelValue,
  isOpen => {
    if (isOpen) {
      loadData()
    }
  },
)

/**
 * Закрытие модального окна
 */
function handleCloseModal() {
  emit('update:modelValue', false)
}

const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal, 'AdverseProbabilityModal')

/**
 * Открытие формы создания нового порога
 */
function openCreateDialog() {
  editingThreshold.value = null
  isFormDialogOpen.value = true
}

/**
 * Открытие формы редактирования порога
 *
 * @param item Порог вероятности
 */
function openEditDialog(item: IAdverseProbabilityThreshold) {
  editingThreshold.value = item
  isFormDialogOpen.value = true
}

/**
 * Запрос на удаление порога
 *
 * @param item Порог вероятности
 */
function requestDelete(item: IAdverseProbabilityThreshold) {
  thresholdToDelete.value = item
  isDeleteConfirmOpen.value = true
}

/**
 * Подтверждение удаления порога
 *
 * @param confirmed true, если подтверждено
 */
async function handleConfirmDelete(confirmed: boolean) {
  isDeleteConfirmOpen.value = false
  if (confirmed && thresholdToDelete.value) {
    await adverseSettingsStore.deleteProbabilityThreshold(thresholdToDelete.value.id)
  }
  thresholdToDelete.value = null
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="1040"
    scrollable
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard class="d-flex flex-column" :data-onboarding="ADVERSE_ANCHORS.settings.probability">
      <!-- Шапка модального окна -->
      <VCardTitle class="d-flex align-center justify-space-between pa-6" :class="smAndDown ? 'px-6 py-4' : 'pa-6'">
        <div class="form_card__title">
          <span class="text-h4 font-weight-bold">
            Пороги вероятности и матрица рисков
          </span>
        </div>
        <CustomCloseButton :close-action="onDialogDismissAttempt" />
      </VCardTitle>

      <!-- Навигационные вкладки -->
      <div class="px-6">
        <VTabs v-model="activeTab">
          <VTab :value="0">
            Пороги вероятности
          </VTab>
          <VTab :value="1">
            Матрица рисков
          </VTab>
        </VTabs>
      </div>

      <!-- Основное содержимое модального окна -->
      <VCardText class="pa-6">
        <!-- Вкладка 0: Пороги вероятности -->
        <div v-if="activeTab === 0" class="d-flex flex-column gap-3">

          <!-- Таблица порогов -->
          <VTable v-if="thresholds.length > 0" class="custom-table w-100">
            <thead>
              <tr>
                <th class="text-left">
                  Уровень вероятности
                </th>
                <th class="text-left">
                  Количество событий
                </th>
                <th class="text-left">
                  Расчетный период
                </th>
                <th class="text-left">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in thresholds" :key="item.id">
                <td>
                  <span>{{ item.probability_display || getProbabilityMeta(item.probability).label }}</span>
                </td>
                <td>
                  <span>{{ item.threshold }}</span>
                </td>
                <td>
                  <span>{{ item.period_display || item.period }}</span>
                </td>
                <td>
                  <div class="d-flex align-center gap-1">
                    <VIconBtn
                      :rounded="false"
                      :size="32"
                      icon="ri-edit-line"
                      variant="text"
                      @click="openEditDialog(item)"
                    />
                    <VIconBtn
                      :rounded="false"
                      :size="32"
                      icon="ri-delete-bin-3-line"
                      variant="text"
                      @click="requestDelete(item)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </VTable>

          <!-- Пустое состояние -->
          <div v-else class="text-center py-8 text-medium-emphasis text-body-1">
            Пороги вероятности не настроены
          </div>
        </div>

        <!-- Вкладка 1: Матрица рисков -->
        <div v-else-if="activeTab === 1" class="d-flex flex-column gap-4">
          <!-- Таблица-матрица -->
          <div class="table-responsive border rounded overflow-hidden">
            <table class="risk-matrix-table w-100">
              <thead>
                <tr>
                  <th class="matrix-corner-th pa-3 text-caption text-medium-emphasis text-center border-b border-e">
                    Вероятность ↓ / Последствия →
                  </th>
                  <th
                    v-for="consequence in consequenceLevels"
                    :key="consequence.value"
                    class="matrix-header-th pa-3 text-center border-b border-e text-no-wrap"
                  >
                    <span class="text-caption font-weight-medium">{{ consequence.label }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="prob in probabilityLevels" :key="prob.value">
                  <!-- Заголовок строки (Вероятность) -->
                  <td class="matrix-row-header-td pa-3 border-b border-e">
                    <span class="text-caption font-weight-medium">{{ prob.label }}</span>
                  </td>

                  <!-- Ячейки матрицы -->
                  <td
                    v-for="consequence in consequenceLevels"
                    :key="`${prob.value}-${consequence.value}`"
                    class="matrix-cell-td pa-2 text-center border-b border-e"
                  >
                    <div
                      class="risk-cell-badge d-flex flex-column align-center justify-center py-2 px-1 rounded"
                      :style="{
                        backgroundColor: `${calculateRiskLevel(prob.value, consequence.value).riskColor}22`,
                        border: `1px solid ${calculateRiskLevel(prob.value, consequence.value).riskColor}44`,
                        color: calculateRiskLevel(prob.value, consequence.value).riskColor,
                      }"
                    >
                      <span class="text-caption font-weight-bold">
                        {{ calculateRiskLevel(prob.value, consequence.value).riskName }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Легенда рисков -->
          <div class="d-flex flex-column gap-2 pt-2">
            <div class="d-flex align-center flex-wrap gap-6">
              <div class="d-flex align-center gap-2">
                <span class="risk-legend-dot" style="background-color: #f44336;" />
                <span class="text-body-2 font-weight-medium">Высокий риск</span>
              </div>
              <div class="d-flex align-center gap-2">
                <span class="risk-legend-dot" style="background-color: #ff9800;" />
                <span class="text-body-2 font-weight-medium">Средний риск</span>
              </div>
              <div class="d-flex align-center gap-2">
                <span class="risk-legend-dot" style="background-color: #4caf50;" />
                <span class="text-body-2 font-weight-medium">Низкий риск</span>
              </div>
            </div>
            <div class="text-caption text-medium-emphasis">
              Уровень риска определяется автоматически на пересечении вероятности возникновения и тяжести последствий нежелательного события.
            </div>
          </div>
        </div>
      </VCardText>

      <!-- Подвал модального окна -->
      <div v-if="activeTab === 0 && canAddThreshold" class="d-flex justify-end pa-6 pt-0">
        <VBtn
          color="primary"
          variant="elevated"
          prepend-icon="ri-add-line"
          @click="openCreateDialog"
        >
          Добавить порог
        </VBtn>
      </div>
    </VCard>

    <!-- Диалоговое окно создания/редактирования порога -->
    <ProbabilityThresholdFormDialog
      v-model="isFormDialogOpen"
      :threshold-item="editingThreshold"
      :occupied-probabilities="occupiedProbabilities"
    />

    <!-- Диалог подтверждения удаления -->
    <ConfirmDialog
      v-model:is-dialog-visible="isDeleteConfirmOpen"
      confirmation-question="Вы уверены, что хотите удалить этот порог вероятности?"
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="handleConfirmDelete"
    />
  </VDialog>
</template>

<style scoped lang="scss">
.custom-table {
  :deep(.v-table__wrapper > table > thead > tr > th),
  :deep(thead th),
  :deep(th) {
    background: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    letter-spacing: 0 !important;
    line-height: 22px !important;
    position: relative;
    padding-inline: 1rem !important;
    white-space: nowrap;
    height: 46px !important;
    border-bottom: none !important;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
      width: 2px;
      height: 14px;
      background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
    }
  }

  :deep(.v-table__wrapper > table > tbody > tr > td),
  :deep(tbody td),
  :deep(td) {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    padding-inline: 1rem !important;
    padding-block: 0.75rem !important;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
    height: 46px !important;
  }

  :deep(tbody tr:hover),
  :deep(tr:hover) {
    background-color: var(--table-row-hover) !important;
  }
}

.risk-matrix-table {
  border-collapse: collapse;

  th,
  td {
    vertical-align: middle;
  }
}

.matrix-corner-th {
  width: 180px;
  background-color: rgba(var(--v-theme-on-surface), 0.02);
  white-space: nowrap;
}

.matrix-header-th {
  min-width: 130px;
  background-color: rgba(var(--v-theme-on-surface), 0.02);
  white-space: nowrap;
}

.matrix-row-header-td {
  width: 180px;
  white-space: nowrap;
}

.matrix-cell-td {
  min-width: 130px;
}

.risk-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
</style>
